// src/pages/SettingsPage.js - FINAL VERSION (ENGLISH)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Skeleton,
  Breadcrumbs,
  Link,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaletteIcon from '@mui/icons-material/Palette';
import SecurityIcon from '@mui/icons-material/Security';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import RestoreIcon from '@mui/icons-material/Restore';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import settingsService from '../services/settingsService';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: '12px' }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const SETTINGS_SECTIONS = [
  { label: 'Company', icon: <BusinessIcon /> },
  { label: 'Notifications', icon: <NotificationsIcon /> },
  { label: 'Theme', icon: <PaletteIcon /> },
  { label: 'Security', icon: <SecurityIcon /> }
];

export default function SettingsPage() {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errors, setErrors] = useState({});

  const [lastSaved, setLastSaved] = useState({
    company: null,
    notifications: null,
    theme: null,
    security: null
  });

  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    rfc: '',
    logo_path: null,
  });

  const [preferences, setPreferences] = useState({
    dark_mode: false,
    primary_color: '#1976d2',
    language: 'en',
    date_format: 'MM/DD/YYYY',
    currency: 'USD',
    email_notifications: true,
    push_notifications: true,
    project_updates: true,
    task_assignments: true,
    deadline_reminders: true,
    client_messages: true,
    weekly_report: false,
    monthly_report: true,
    two_factor_auth: false,
    session_timeout: 30,
    password_expiration: 90,
    allow_multiple_sessions: true,
  });

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      
      const [companyData, preferencesData] = await Promise.all([
        settingsService.getCompanySettings(),
        settingsService.getUserPreferences(),
      ]);

      if (companyData.success) {
        setCompanySettings(companyData.settings);
      }

      if (preferencesData.success) {
        setPreferences(preferencesData.preferences);
      }
    } catch (error) {
      showMessage('Error loading settings', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to leave? You have unsaved changes.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const validateCompanySettings = () => {
    const newErrors = {};
    
    if (!companySettings.company_name?.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    
    if (companySettings.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companySettings.email)) {
      newErrors.email = 'Invalid email';
    }
    
    if (companySettings.phone && !/^[\d\s+\-()]+$/.test(companySettings.phone)) {
      newErrors.phone = 'Invalid phone format';
    }
    
    if (companySettings.website && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(companySettings.website)) {
      newErrors.website = 'Invalid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showMessage('File is too large. Max 2MB', 'error');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showMessage('Only images are allowed', 'error');
      return;
    }

    setLogoFile(file);
    setHasUnsavedChanges(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCompany = async () => {
    if (!validateCompanySettings()) {
      showMessage('Please fix errors before saving', 'error');
      return;
    }

    try {
      setSaving(true);

      if (logoFile) {
        setUploadProgress(10);
        const logoResponse = await settingsService.uploadLogo(logoFile);
        setUploadProgress(50);
        
        if (logoResponse.success) {
          setCompanySettings(prev => ({ ...prev, logo_path: logoResponse.logoPath }));
          setUploadProgress(100);
        }
        setLogoFile(null);
        setLogoPreview(null);
      }

      const response = await settingsService.updateCompanySettings(companySettings);
      
      if (response.success) {
        showMessage('✅ Company settings saved successfully');
        setLastSaved({ ...lastSaved, company: new Date() });
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      showMessage('❌ Error saving settings', 'error');
      console.error('Error:', error);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setSaving(true);
      const notificationPrefs = {
        email_notifications: preferences.email_notifications,
        push_notifications: preferences.push_notifications,
        project_updates: preferences.project_updates,
        task_assignments: preferences.task_assignments,
        deadline_reminders: preferences.deadline_reminders,
        client_messages: preferences.client_messages,
        weekly_report: preferences.weekly_report,
        monthly_report: preferences.monthly_report,
      };

      const response = await settingsService.updateUserPreferences(notificationPrefs);
      
      if (response.success) {
        showMessage('✅ Notification preferences saved');
        setLastSaved({ ...lastSaved, notifications: new Date() });
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      showMessage('❌ Error saving preferences', 'error');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTheme = async () => {
    try {
      setSaving(true);
      const themePrefs = {
        dark_mode: preferences.dark_mode,
        primary_color: preferences.primary_color,
        language: preferences.language,
        date_format: preferences.date_format,
        currency: preferences.currency,
      };

      const response = await settingsService.updateUserPreferences(themePrefs);
      
      if (response.success) {
        localStorage.setItem('themeMode', preferences.dark_mode ? 'dark' : 'light');
        localStorage.setItem('darkMode', preferences.dark_mode ? 'true' : 'false');
        localStorage.setItem('userPreferences', JSON.stringify({
          ...preferences,
          dark_mode: preferences.dark_mode
        }));
        showMessage('✅ Theme saved. Reloading...', 'success');
        setLastSaved({ ...lastSaved, theme: new Date() });
        setHasUnsavedChanges(false);
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      showMessage('❌ Error saving theme', 'error');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    try {
      setSaving(true);
      const securityPrefs = {
        two_factor_auth: preferences.two_factor_auth,
        session_timeout: preferences.session_timeout,
        password_expiration: preferences.password_expiration,
        allow_multiple_sessions: preferences.allow_multiple_sessions,
      };

      const response = await settingsService.updateUserPreferences(securityPrefs);
      
      if (response.success) {
        showMessage('✅ Security settings saved');
        setLastSaved({ ...lastSaved, security: new Date() });
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      showMessage('❌ Error saving settings', 'error');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Reset all settings to defaults? This action cannot be undone.')) {
      setCompanySettings({
        company_name: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        rfc: '',
        logo_path: null,
      });
      setPreferences({
        dark_mode: false,
        primary_color: '#1976d2',
        language: 'en',
        date_format: 'MM/DD/YYYY',
        currency: 'USD',
        email_notifications: true,
        push_notifications: true,
        project_updates: true,
        task_assignments: true,
        deadline_reminders: true,
        client_messages: true,
        weekly_report: false,
        monthly_report: true,
        two_factor_auth: false,
        session_timeout: 30,
        password_expiration: 90,
        allow_multiple_sessions: true,
      });
      showMessage('⚠️ Settings reset. Do not forget to save changes.', 'warning');
      setHasUnsavedChanges(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Skeleton variant="rectangular" height={150} sx={{ mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const colorOptions = [
    { name: 'Blue', color: '#1976d2' },
    { name: 'Green', color: '#2e7d32' },
    { name: 'Orange', color: '#ed6c02' },
    { name: 'Purple', color: '#9c27b0' },
    { name: 'Red', color: '#d32f2f' },
    { name: 'Teal', color: '#00897b' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        sx={{ mb: 1 }}
        aria-label="breadcrumb"
      >
        <Link
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          color="inherit"
          onClick={() => navigate('/dashboard')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Dashboard
        </Link>
        <Typography 
          sx={{ display: 'flex', alignItems: 'center' }} 
          color="text.primary"
        >
          <SettingsIcon sx={{ mr: 0.5 }} fontSize="small" />
          Settings
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.35, md: 1.6 },
          mb: 1.5,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 0.8, display: 'flex' }}>
              <SettingsIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.3rem' } }}>
                Settings
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Customize your system experience
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleResetToDefaults}
            size="small"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Reset
          </Button>
        </Box>
      </Paper>

      {/* Unsaved Changes Banner */}
      {hasUnsavedChanges && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setHasUnsavedChanges(false)}>
              Ignore
            </Button>
          }
        >
          You have unsaved changes
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
        {isMobile ? (
          <Box sx={{ p: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
            <TextField
              fullWidth
              select
              size="small"
              label="Settings section"
              value={tabValue}
              onChange={(event) => setTabValue(Number(event.target.value))}
            >
              {SETTINGS_SECTIONS.map((section, index) => (
                <MenuItem key={section.label} value={index}>{section.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        ) : (
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { minHeight: 46, py: 0.8 },
            }}
          >
            {SETTINGS_SECTIONS.map((section) => (
              <Tab
                key={section.label}
                icon={section.icon}
                label={section.label}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>
        )}

        {/* Tab 1: Company Settings */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: { xs: 1.35, md: 1.6 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Company Information
            </Typography>
            {lastSaved.company && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Last updated: {lastSaved.company.toLocaleString('en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}

            <Grid container spacing={1.5}>
              {/* Logo */}
              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Company Logo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Avatar
                      src={
                        logoPreview || 
                        (companySettings.logo_path ? settingsService.getLogoUrl(companySettings.logo_path) : null)
                      }
                      sx={{
                        width: 100,
                        height: 100,
                        bgcolor: 'primary.main',
                        fontSize: '2rem',
                      }}
                    >
                      {!logoPreview && !companySettings.logo_path && 
                        companySettings.company_name?.split(' ').map(n => n[0]).join('').toUpperCase()
                      }
                    </Avatar>
                    <Box>
                      <Button
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        component="label"
                      >
                        {logoFile ? 'Change Logo' : 'Upload Logo'}
                        <input 
                          type="file" 
                          hidden 
                          accept="image/*" 
                          onChange={handleLogoChange}
                        />
                      </Button>
                      {logoFile && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                          ✓ {logoFile.name}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                        Formats: JPG, PNG. Max size: 2MB
                      </Typography>
                    </Box>
                  </Box>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="caption" color="text.secondary">
                        Uploading logo... {uploadProgress}%
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companySettings.company_name}
                  onChange={(e) => {
                    setCompanySettings({ ...companySettings, company_name: e.target.value });
                    setHasUnsavedChanges(true);
                    if (errors.company_name) {
                      setErrors({ ...errors, company_name: null });
                    }
                  }}
                  error={!!errors.company_name}
                  helperText={errors.company_name}
                  required
                  InputProps={{
                    startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tax ID / RFC"
                  value={companySettings.rfc}
                  onChange={(e) => {
                    setCompanySettings({ ...companySettings, rfc: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={companySettings.email}
                  onChange={(e) => {
                    setCompanySettings({ ...companySettings, email: e.target.value });
                    setHasUnsavedChanges(true);
                    if (errors.email) {
                      setErrors({ ...errors, email: null });
                    }
                  }}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={companySettings.phone}
                  onChange={(e) => {
                    setCompanySettings({ ...companySettings, phone: e.target.value });
                    setHasUnsavedChanges(true);
                    if (errors.phone) {
                      setErrors({ ...errors, phone: null });
                    }
                  }}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={companySettings.website}
                  onChange={(e) => {
                    setCompanySettings({ ...companySettings, website: e.target.value });
                    setHasUnsavedChanges(true);
                    if (errors.website) {
                      setErrors({ ...errors, website: null });
                    }
                  }}
                  error={!!errors.website}
                  helperText={errors.website}
                  InputProps={{
                    startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={companySettings.address}
                  onChange={(e) => {
                    setCompanySettings({ ...companySettings, address: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveCompany}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 2: Notifications */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Notification Settings
            </Typography>
            {lastSaved.notifications && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Last updated: {lastSaved.notifications.toLocaleString('en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose how and when you want to receive notifications
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Notification Channels
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.email_notifications}
                          onChange={(e) => {
                            setPreferences({ ...preferences, email_notifications: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Email Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.push_notifications}
                          onChange={(e) => {
                            setPreferences({ ...preferences, push_notifications: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Push Notifications"
                      sx={{ display: 'block' }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Notification Types
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.project_updates}
                          onChange={(e) => {
                            setPreferences({ ...preferences, project_updates: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Project Updates"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.task_assignments}
                          onChange={(e) => {
                            setPreferences({ ...preferences, task_assignments: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Task Assignments"
                      sx={{ display: 'block' }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.deadline_reminders}
                          onChange={(e) => {
                            setPreferences({ ...preferences, deadline_reminders: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Deadline Reminders"
                      sx={{ display: 'block' }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.client_messages}
                          onChange={(e) => {
                            setPreferences({ ...preferences, client_messages: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Client Messages"
                      sx={{ display: 'block' }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Automatic Reports
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.weekly_report}
                          onChange={(e) => {
                            setPreferences({ ...preferences, weekly_report: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Weekly Report"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.monthly_report}
                          onChange={(e) => {
                            setPreferences({ ...preferences, monthly_report: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label="Monthly Report"
                      sx={{ display: 'block' }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveNotifications}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 3: Theme */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Theme Customization
            </Typography>
            {lastSaved.theme && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Last updated: {lastSaved.theme.toLocaleString('en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.dark_mode}
                          onChange={(e) => {
                            setPreferences({ ...preferences, dark_mode: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Dark Mode
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enable dark mode to reduce eye strain
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  System Primary Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {colorOptions.map((option) => (
                    <Tooltip key={option.color} title={option.name}>
                      <Box
                        onClick={() => {
                          setPreferences({ ...preferences, primary_color: option.color });
                          setHasUnsavedChanges(true);
                        }}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          bgcolor: option.color,
                          cursor: 'pointer',
                          border: preferences.primary_color === option.color ? '3px solid' : '2px solid',
                          borderColor: preferences.primary_color === option.color ? 'text.primary' : 'divider',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: 3
                          }
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={preferences.language}
                    label="Language"
                    onChange={(e) => {
                      setPreferences({ ...preferences, language: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <MenuItem value="en">🇺🇸 English</MenuItem>
                    <MenuItem value="es">🇲🇽 Spanish</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Date Format</InputLabel>
                  <Select
                    value={preferences.date_format}
                    label="Date Format"
                    onChange={(e) => {
                      setPreferences({ ...preferences, date_format: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                    <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                    <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={preferences.currency}
                    label="Currency"
                    onChange={(e) => {
                      setPreferences({ ...preferences, currency: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <MenuItem value="USD">USD - US Dollar</MenuItem>
                    <MenuItem value="MXN">MXN - Mexican Peso</MenuItem>
                    <MenuItem value="EUR">EUR - Euro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveTheme}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save and Apply Theme'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 4: Security */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Security Settings
            </Typography>
            {lastSaved.security && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Last updated: {lastSaved.security.toLocaleString('en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Protect your account and data
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.two_factor_auth}
                          onChange={(e) => {
                            setPreferences({ ...preferences, two_factor_auth: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Two-Factor Authentication (2FA)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Add an extra layer of security to your account
                            </Typography>
                          </Box>
                          <Tooltip 
                            title="Requires a verification code sent to your email each time you log in"
                            arrow
                          >
                            <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Session Timeout</InputLabel>
                  <Select
                    value={preferences.session_timeout}
                    label="Session Timeout"
                    onChange={(e) => {
                      setPreferences({ ...preferences, session_timeout: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Password Expiration</InputLabel>
                  <Select
                    value={preferences.password_expiration}
                    label="Password Expiration"
                    onChange={(e) => {
                      setPreferences({ ...preferences, password_expiration: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <MenuItem value={30}>30 days</MenuItem>
                    <MenuItem value={60}>60 days</MenuItem>
                    <MenuItem value={90}>90 days</MenuItem>
                    <MenuItem value={0}>Never</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.allow_multiple_sessions}
                          onChange={(e) => {
                            setPreferences({ ...preferences, allow_multiple_sessions: e.target.checked });
                            setHasUnsavedChanges(true);
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Allow Multiple Sessions
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Allow logging in from multiple devices simultaneously
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveSecurity}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
